import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { formatDate, toLocalDateTime, validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { deletePRD3020_R01, downloadExcel_PRD3020_R01, getPRD3020_R01AllCount, getPRD3020_R01DetailList, getPRD3020_R01List, patchPRD3020_R01, postPRD3020_R01 } from "./Api_PRD3020_R01";
import { defaultPRD3020_R01Dto, PRD3020_R01Dto } from "./Dto_PRD3020_R01";
import { defaultPRD3020_R01Filter, PRD3020_R01Filter, usePRD3020_R01Store } from "./Store_PRD3020_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateEmployeeCd } from "src/utils/grid/GridValidateContent";


interface OrderContentProps {
	gridRef: React.RefObject<AgGridReact<PRD3020_R01Dto> | null>;
	setMessage: (message: string) => void;  
	
}	


export const Content_PRD3020_R01 = (props: OrderContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		PRD3020_R01Filter, setPRD3020_R01Filter,
		PRD3020_R01PastFilter, setPRD3020_R01PastFilter,
		PRD3020_R01List, setPRD3020_R01List,
		setPRD3020_R01DetailList,
		editPRD3020_R01, setEditPRD3020_R01,
		insertPRD3020_R01List, removePRD3020_R01List,
		deletedPRD3020_R01List, addDeletedPRD3020_R01List, removeDeletedPRD3020_R01List,
		selectedPRD3020_R01, setSelectedPRD3020_R01, initSelectedPRD3020_R01,
		originalPRD3020_R01List, setOriginalPRD3020_R01List,
		allCount, setAllCount,
		modalVisible, setModalVisible,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = usePRD3020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { compCdList, downtimeValueList, workPlaceList, jobTeamList, ordTypeList, employeeCdList,  } = useCommonCodeStore();


	const setAuth = useActionbarAuthStore((state) => state.setAuth);



	// 권한 정보 로드
	useFetchActionbarAuth("PRD3020_R01");

	const getParams = (filter: Partial<PRD3020_R01Filter>) => {
		return {
			compCd: filter.compCd,
			workplaceCd: filter.workplaceCd,
			jobTeam: filter.jobTeam,
			dateRange: filter.dateRange,
			closeTag: filter.closeTag,
		}
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getPRD3020_R01AllCount(getParams(PRD3020_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [PRD3020_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch') => {
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
			setDetailScrollTop(null);
		}
		setSelectedPRD3020_R01(undefined);
		setPRD3020_R01DetailList([]);
	
		const initialListLength = PRD3020_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setPRD3020_R01List([]);
		}

		try {
			const params = getParams(PRD3020_R01Filter);
			const [responseData, detailResponseData] = await Promise.all([
				getPRD3020_R01List(
					'',
					params,
					fetchType === 'refetch' ? initialListLength : PRD3020_R01List.length ?? 0
				),
				getPRD3020_R01DetailList(params),
			]);

			const newList = responseData.data.data ?? [];
			const newDetailList = detailResponseData.data.data ?? [];
			setPRD3020_R01List(newList);
			setPRD3020_R01DetailList(newDetailList);

			const processAllCount = await renewalAllCount();
			setOriginalPRD3020_R01List(newList);
			removeDeletedPRD3020_R01List(deletedPRD3020_R01List);
			if (fetchType === 'search') {
				gridRef.current?.api?.ensureIndexVisible(0, 'top');
			}

			if (newList.length === 0) {
				// insertPRD3020_R01List([{ ...defaultPRD3020_R01Row, inserted: true }], 0);
				showWarning('데이터가 없습니다.');
			} else {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}

			setPRD3020_R01PastFilter(PRD3020_R01Filter);
		} catch {
			setPRD3020_R01List([]);
			setOriginalPRD3020_R01List([]);
			setPRD3020_R01DetailList([]);
			setSelectedPRD3020_R01(undefined);
			showWarning('데이터 조회에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		PRD3020_R01Filter, PRD3020_R01List, deletedPRD3020_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalPRD3020_R01List, setSelectedPRD3020_R01, setPRD3020_R01DetailList,
		setPRD3020_R01List, setPRD3020_R01PastFilter, removeDeletedPRD3020_R01List,
		setScrollTop, setDetailScrollTop, renewalAllCount,
	]);

	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	const fetchOne = useCallback(async (data: PRD3020_R01Dto) => {
		const todayZero = new Date();
		todayZero.setHours(0, 0, 0, 0);

		setEditPRD3020_R01({
			...defaultPRD3020_R01Dto,
			...data,
			workStartDt: toLocalDateTime(todayZero),
			workEndDt: toLocalDateTime(todayZero),
			downtimeStartTime: toLocalDateTime(todayZero),
			downtimeStartDt: formatDate(todayZero),
			downtimeEndTime: toLocalDateTime(todayZero),
			downtimeEndDt: formatDate(todayZero),
			downtimeCase: downtimeValueList[0].minorCd,
		});
	}, []);


	// OrderList가 변경될 때마다 원본 데이터 복사
	useEffect(() => {
		setOriginalPRD3020_R01List(JSON.parse(JSON.stringify(PRD3020_R01List)));
	}, [PRD3020_R01List]);

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedPRD3020_R01List.length === 0 && selectedPRD3020_R01 === null,
			save: PRD3020_R01List.length === 0,
			print: PRD3020_R01List.length === 0,
			excel: PRD3020_R01List.length === 0
		}));
	}, [selectedPRD3020_R01, deletedPRD3020_R01List, PRD3020_R01List]);
	
	/**
	 * 엑셀 다운로드 핸들러
	 * 현재 검색 조건의 사용자 목록을 엑셀 파일로 다운로드
	 */
	const handleExcelClick = useCallback(async () => {

		const response = await downloadExcel_PRD3020_R01(
			'',
			getParams(PRD3020_R01Filter),
			PRD3020_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '작업지시정보등록-excel.xlsx' });
	}, [PRD3020_R01Filter])
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultPRD3020_R01Row = useCallback(() => {

		const workPlaceCdListToUse = workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');

		return {
			...defaultPRD3020_R01Dto,
			compCd: compCdList[0].compCd,
			orderType: ordTypeList[0].minorCd,
			workplaceCd: workPlaceCdListToUse[0].workplaceCd,
			jobTeam: jobTeamList[0].minorCd,
			workProcess: workPlaceCdListToUse[0].workProcess,
			downtimeCase: downtimeValueList[0].minorCd,
			inserted: true
		}
	}, [defaultPRD3020_R01Dto, compCdList, ordTypeList, workPlaceList, jobTeamList]);



	const validateInsertData = (
		data: PRD3020_R01Dto,
	) => {

		let warningMessage = '(지시No 조회필요)';
		
		if (!validateString(data.productOrderNo)) {
			showWarning('지시No를 입력해주세요.'); return false;
		}
		if (!validateString(data.compCd)) {
			showWarning('지시정보가 없습니다. 사업장을 선택해주세요.' + warningMessage); return false;
		}
		if (!validateString(data.workplaceCd)) {
			showWarning('공정명을 선택해주세요.' + warningMessage); return false;
		}
		if (!validateString(data.itemCd)) {
			showWarning('품목코드를 입력해주세요.' + warningMessage); return false;
		}
		if (!validateString(data.productEmp)) {
			showWarning('담당자를 입력해주세요.' + warningMessage); return false;
		}
		if (!validateEmployeeCd(data.productEmp, employeeCdList)) {
			showWarning('담당자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}

		//시작시, 종료시는 24 이내
		if (data.downtimeStartHour < 0 || data.downtimeStartHour > 23) {
			showWarning('시작시간은 24 이내여야 합니다.'); return false;
		}
		if (data.downtimeEndHour < 0 || data.downtimeEndHour > 23) {
			showWarning('종료시간은 24 이내여야 합니다.'); return false;
		}

		//시작분, 종료분은 60 이내
		if (data.downtimeStartMinute < 0 || data.downtimeStartMinute > 59) {
			showWarning('시작분은 60 이내여야 합니다.'); return false;
		}
		if (data.downtimeEndMinute < 0 || data.downtimeEndMinute > 59) {
			showWarning('종료분은 60 이내여야 합니다.'); return false;
		}

		//시작시간과 종료시간 조합해서 시-분-초 만들기
		const workStartDt = new Date(data.downtimeStartDt!);
		workStartDt.setHours(data.downtimeStartHour, data.downtimeStartMinute, 0);
		const workEndDt = new Date(data.downtimeEndDt!);
		workEndDt.setHours(data.downtimeEndHour, data.downtimeEndMinute, 0);

		if (workStartDt > workEndDt) {
			showWarning('시작시간은 종료시간보다 이전이어야 합니다.'); return false;
		}

		data.downtimeStartDt = formatDate(workStartDt);
		data.downtimeEndDt = formatDate(workEndDt);

		return true;
	}
	
	const handleSaveClick = useCallback(async () => {
		//조회 페이지
		let valid = true;
		if(!validateInsertData(editPRD3020_R01!)) valid = false;
		if(!valid) { return; }

		//downtimeStartTime, downtimeEndTime 9시간 추가해서 시차보정하기
		const downtimeStartTime = new Date(editPRD3020_R01!.downtimeStartTime!);
		// downtimeStartTime.setHours(downtimeStartTime.getHours() + 9);
		const downtimeEndTime = new Date(editPRD3020_R01!.downtimeEndTime!);
		// downtimeEndTime.setHours(downtimeEndTime.getHours() + 9);

		const postResponse = await postPRD3020_R01({
			...editPRD3020_R01!,
			downtimeStartTime: toLocalDateTime(downtimeStartTime),
			downtimeEndTime: toLocalDateTime(downtimeEndTime),
		});

		if(postResponse.data.successFlag) {
			showSuccess('비가동시간 등록에 성공하였습니다.');
			setMessage('비가동시간 등록에 성공하였습니다.');
			const selectedRow = PRD3020_R01List.find(
				(row) => row.productResultNo === editPRD3020_R01?.productResultNo
			);
			if (selectedRow) {
				setSelectedPRD3020_R01(selectedRow);
			}
			setModalVisible(false);

			try {
				const detailResponseData = await getPRD3020_R01DetailList(
					getParams(PRD3020_R01PastFilter)
				);
				setPRD3020_R01DetailList(detailResponseData.data.data ?? []);
			} catch {
				showWarning('비가동시간은 등록되었으나 상세내역 재조회에 실패했습니다.');
			}
		}else {
			showWarning('비가동시간 등록에 실패하였습니다.');
			return;
		}


	}, [
		editPRD3020_R01,
		PRD3020_R01List,
		PRD3020_R01PastFilter,
		setModalVisible,
		setMessage,
		setPRD3020_R01DetailList,
		setSelectedPRD3020_R01,
	]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		//조회 페이지
	}, [selectedPRD3020_R01, deletedPRD3020_R01List, addDeletedPRD3020_R01List, removeDeletedPRD3020_R01List, setPRD3020_R01List, PRD3020_R01List]);

	

	return{
		fetchData,
		fetchOne,
		defaultPRD3020_R01Row,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		buttonDisabled,
	}
}
